import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import PrintSettings from '../components/PrintSettings';
import SupportTicketForm from '../components/SupportTicketForm';
import HelpModal from '../components/HelpModal';
import SalesChart from '../components/SalesChart';
import TopProducts from '../components/TopProducts';
import { button as buttonStyle } from '../styles/ui';
import { useDispatch, useSelector } from 'react-redux';
import { logout, reset } from '../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import http from '../utils/request';
import { hasPerm, PERM_KEYS } from '../utils/permissions';
import { getTenantInfo } from '../features/tenant/tenantSlice';
import { getFiscalConfig, getFiscalStatus } from '../features/fiscal/fiscalSlice';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import useSubscriptionRestrictions from '../hooks/useSubscriptionRestrictions';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { tenant } = useSelector((state) => state.tenant || {});
  const { fiscalConfig, fiscalStatus } = useSelector((state) => state.fiscal || {});
  const { daysOverdue } = useSubscriptionRestrictions();
  const isSubscriptionExpired = daysOverdue > 0;

  // Detectar modo Ecommerce Lite / Restaurante
  const serviceType = tenant?.serviceType || user?.tenantId?.serviceType || 'full_pos';
  const isEcommerceLite = serviceType === 'ecommerce_lite';
  const isRestaurant = serviceType === 'restaurant';

  const [stats, setStats] = useState({
    todaySales: 0,
    todayRevenue: 0,
    lowStockCount: 0,
    totalProducts: 0,
    averageTicket: 0,
    criticalStock: 0,
  });
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showSecretStats, setShowSecretStats] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setFetchError(null);
      setLoading(true);
      
      const res = await http.get('/sales/dashboard-stats');
      const data = res.data;

      setStats({
        todaySales: data.todaySales || 0,
        todayRevenue: data.todayRevenue || 0,
        lowStockCount: data.lowStockCount || 0,
        totalProducts: data.totalProducts || 0,
        averageTicket: data.averageTicket || 0,
        criticalStock: data.criticalStock || 0,
      });

      setRecentSales(data.recentSales || []);
    } catch (err) {
      console.error('Error al cargar datos del dashboard:', err);
      try {
        const status = err?.response?.status;
        const msg = err?.response?.data?.message || err?.message || 'Error desconocido';
        const nice = `No se pudieron cargar datos (${status || 'sin código'}): ${msg}`;
        setFetchError(nice);
        if (status === 401 || status === 403) {
          window.location.href = '/login';
        }
      } catch (_) {
        setFetchError('Error desconocido al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Cargar info del comercio y fiscal si falta
  useEffect(() => {
    if (!tenant) dispatch(getTenantInfo());
    if (!fiscalConfig) dispatch(getFiscalConfig());
    // Obtener estado de locks para decidir si permitimos acceder a la UI fiscal
    if (!fiscalStatus) dispatch(getFiscalStatus());
  }, [dispatch, tenant, fiscalConfig, fiscalStatus]);

  // Mostrar ayuda automática para tenants nuevos: comprobamos si ya se mostró
  useEffect(() => {
    try {
      const tenantId = tenant?._id || tenant?.id;
      if (!tenantId) return;
      const key = `help_shown_${tenantId}`;
      const shown = localStorage.getItem(key);
      if (!shown) {
        // abrir modal por primera vez
        setShowHelp(true);
      }
    } catch (e) {
      // si localStorage no está disponible, no bloquear
      console.warn('No se pudo comprobar ayuda automática:', e);
    }
  }, [tenant]);

  // Si el usuario cierra sesión mientras está en el dashboard,
  // redirigir inmediatamente al login.
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(reset());
    navigate('/login');
  };

  // Aviso de prueba se maneja desde SubscriptionBanner

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', fontFamily: 'Arial, sans-serif' }}>
      <div className="container-safe" style={{ display: 'flex', padding: '16px', gap: 16, width: '100%', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
        {/* Sidebar restaurado sin botones Empresa, Fiscal, Usuarios */}
        <aside className="hide-on-mobile" style={{ width: 220, flex: '0 0 220px', display: 'flex', flexDirection: 'column', gap: 0, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', paddingRight: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Acciones</div>
          {hasPerm(user, PERM_KEYS.PRODUCTS_ACCESS) && (
            <button onClick={() => navigate('/products')} style={{ ...smallButton, backgroundColor: buttonGreen.backgroundColor }}>🛒 Productos</button>
          )}
          {hasPerm(user, PERM_KEYS.SALES_ACCESS) && !isEcommerceLite && !isRestaurant && (
            <button onClick={() => navigate('/sales')} style={{ ...smallButton, backgroundColor: buttonBlue.backgroundColor }}>💵 Punto de Venta</button>
          )}
          {isRestaurant && (
            <>
              <button onClick={() => navigate('/salon')} style={{ ...smallButton, backgroundColor: buttonBlue.backgroundColor }}>🍽️ Salón (Mesas)</button>
              <button onClick={() => navigate('/kitchen')} style={{ ...smallButton, backgroundColor: '#6f42c1' }}>🍳 Cocina (KDS)</button>
            </>
          )}
          {hasPerm(user, PERM_KEYS.SALES_HISTORY) && (
            <button 
              onClick={() => isSubscriptionExpired ? toast.error('Función bloqueada: suscripción vencida.') : navigate('/sales-history')} 
              style={{ 
                ...smallButton, 
                backgroundColor: isSubscriptionExpired ? '#cbd5e0' : buttonCyan.backgroundColor,
                cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
                color: isSubscriptionExpired ? '#666' : 'white',
              }}
              title={isSubscriptionExpired ? "Suscripción vencida - Bloqueado" : ""}
            >📜 Historial</button>
          )}
          {hasPerm(user, PERM_KEYS.INVOICE_ACCESS) && (
            <button 
              onClick={() => isSubscriptionExpired ? toast.error('Función bloqueada: suscripción vencida.') : navigate('/invoices')} 
              style={{ 
                ...smallButton, 
                backgroundColor: isSubscriptionExpired ? '#cbd5e0' : '#9c27b0',
                cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
                color: isSubscriptionExpired ? '#666' : 'white',
              }}
              title={isSubscriptionExpired ? "Suscripción vencida - Bloqueado" : ""}
            >🧾 Facturas</button>
          )}
          {hasPerm(user, PERM_KEYS.CUSTOMERS_ACCESS) && (
            <button onClick={() => navigate('/customers')} style={{ ...smallButton, backgroundColor: buttonOrange.backgroundColor }}>👥 Clientes</button>
          )}
          {hasPerm(user, PERM_KEYS.CUSTOMERS_ACCESS) && !isEcommerceLite && (
            <button 
              onClick={() => isSubscriptionExpired ? toast.error('Función bloqueada: suscripción vencida.') : navigate('/cuentas-corrientes')} 
              style={{ 
                ...smallButton, 
                backgroundColor: isSubscriptionExpired ? '#cbd5e0' : '#8e44ad',
                cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
                color: isSubscriptionExpired ? '#666' : 'white',
              }}
              title={isSubscriptionExpired ? "Suscripción vencida - Bloqueado" : ""}
            >📒 Cuentas Corrientes</button>
          )}
          {hasPerm(user, PERM_KEYS.SUPPLIERS_ACCESS) && !isEcommerceLite && (
            <button onClick={() => navigate('/suppliers')} style={{ ...smallButton, backgroundColor: '#3f51b5' }}>📦 Proveedores</button>
          )}
          {hasPerm(user, PERM_KEYS.PURCHASES_ACCESS) && !isEcommerceLite && (
            <button 
              onClick={() => isSubscriptionExpired ? toast.error('Función bloqueada: suscripción vencida.') : navigate('/purchases')} 
              style={{ 
                ...smallButton, 
                backgroundColor: isSubscriptionExpired ? '#cbd5e0' : '#009688',
                cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
                color: isSubscriptionExpired ? '#666' : 'white',
              }}
              title={isSubscriptionExpired ? "Suscripción vencida - Bloqueado" : ""}
            >🧾 Compras</button>
          )}
          {hasPerm(user, PERM_KEYS.PRODUCTS_ACCESS) && !isEcommerceLite && (
            <button onClick={() => navigate('/stock-movements')} style={{ ...smallButton, backgroundColor: '#ff9800' }}>📊 Movimientos</button>
          )}
          {hasPerm(user, PERM_KEYS.CASH_ACCESS) && !isEcommerceLite && (
            <button 
              onClick={() => isSubscriptionExpired ? toast.error('Función bloqueada: suscripción vencida.') : navigate('/cash-register')} 
              style={{ 
                ...smallButton, 
                backgroundColor: isSubscriptionExpired ? '#cbd5e0' : buttonTeal.backgroundColor,
                cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
                color: isSubscriptionExpired ? '#666' : 'white',
              }}
              title={isSubscriptionExpired ? "Suscripción vencida - Bloqueado" : ""}
            >💰 Caja</button>
          )}
          {hasPerm(user, PERM_KEYS.REPORTS_ACCESS) && !isEcommerceLite && (
            <button 
              onClick={() => isSubscriptionExpired ? toast.error('Función bloqueada: suscripción vencida.') : navigate('/reports')} 
              style={{ 
                ...smallButton, 
                backgroundColor: isSubscriptionExpired ? '#cbd5e0' : buttonPurple.backgroundColor,
                cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
                color: isSubscriptionExpired ? '#666' : 'white',
              }}
              title={isSubscriptionExpired ? "Suscripción vencida - Bloqueado" : ""}
            >📊 Reportes</button>
          )}
          {/* E-commerce - Solo owner/admin */}
          {/* E-commerce - Solo owner/admin y si está habilitado y NO es restaurante */}
          {(user?.role === 'owner' || user?.role === 'admin') && (isEcommerceLite || tenant?.modules?.ecommerce?.enabled) && !isRestaurant && (
            <button 
              onClick={() => isSubscriptionExpired ? toast.error('Función bloqueada: suscripción vencida.') : navigate('/ecommerce')} 
              style={{ 
                ...smallButton, 
                backgroundColor: isSubscriptionExpired ? '#cbd5e0' : '#FFE600', 
                color: isSubscriptionExpired ? '#666' : '#000',
                cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
              }}
              title={isSubscriptionExpired ? "Suscripción vencida - Bloqueado" : ""}
            >🛍️ E-commerce</button>
          )}
          {/* Botón configuración central opcional (no depende de empresa/fiscal/usuarios para mostrarse aquí) */}
          <button onClick={() => navigate('/settings')} style={{ ...smallButton, backgroundColor: '#2d3436' }}>🛠️ Configuración</button>

          {/* Botón Soporte */}
          <div>
            <SupportTicketForm buttonStyle={{ ...smallButton, backgroundColor: '#ff7a59' }} />
          </div>
          <button onClick={handleLogout} style={{ ...smallButton, backgroundColor: buttonRed.backgroundColor }}>🚪 Cerrar sesión</button>
        </aside>

        {/* Botón Soporte para móvil: se muestra como botón flotante en vista móvil */}
        <div className="show-on-mobile" style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 999 }}>
          <SupportTicketForm buttonStyle={{ padding: '12px 16px', borderRadius: 999, backgroundColor: '#ff7a59', color: 'white', border: 'none', boxShadow: '0 6px 18px rgba(0,0,0,0.15)' }} />
        </div>

        {/* Main content area */}
        <main style={{ flex: 1 }}>
          {/* Banner Ventio: logo (izquierda), título (centro), claim (derecha) */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #22c55e 55%, #16a34a 100%)',
            color: 'white',
            borderRadius: 16,
            padding: '16px 20px',
            marginBottom: 16,
            boxShadow: '0 10px 24px rgba(14,165,233,0.25)',
            flexWrap: 'wrap',
            minHeight: 80
          }}>
            {/* Izquierda: solo logo */}
            <img
              src={process.env.PUBLIC_URL + '/branding/ventio-logo.svg'}
              alt="Ventio"
              style={{ height: 56, width: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))' }}
            />

            {/* Centro: título */}
            <div style={{ flex: 1, textAlign: 'center', minWidth: 220 }}>
              <h1 style={{
                margin: 0,
                fontSize: 26,
                fontWeight: 800,
                letterSpacing: 0.3,
                textShadow: '0 1px 1px rgba(0,0,0,0.35)'
              }}>{isEcommerceLite ? 'Panel de Control E-commerce' : 'Bienvenido al Dashboard'}</h1>
            </div>

            {/* Derecha: claim Ventio */}
            <div style={{ textAlign: 'right', minWidth: 220 }}>
              <div style={{ fontWeight: 800, fontSize: 20 }}>Ventio</div>
              <div style={{ opacity: 0.95, fontSize: 14 }}>Sistema POS en la nube</div>
              <div style={{ marginTop: 8 }}>
                <button
                  onClick={() => setShowHelp(true)}
                  style={{ ...buttonStyle('gray'), background: 'rgba(255,255,255,0.12)', color: 'white', padding: '8px 10px', borderRadius: 8 }}
                  aria-label="Abrir ayuda"
                >
                  ❓ Ayuda rápida
                </button>
              </div>
            </div>
          </div>

          {/* Navegación móvil vía sidebar; sin duplicar acciones aquí */}

          {/* El aviso superior compacto se muestra ahora en SubscriptionBanner (arriba de todo) */}

          {loading ? (
            <p>Cargando información...</p>
          ) : fetchError ? (
            <div style={{ background: 'white', padding: 20, borderRadius: 8 }}>
              <h3>Error cargando datos</h3>
              <p style={{ color: '#a00' }}>{fetchError}</p>
              <div style={{ marginTop: 12 }}>
                <button onClick={() => fetchDashboardData()} style={{ padding: '8px 12px', borderRadius: 6, background: '#0b74de', color: 'white', border: 'none' }}>Reintentar</button>
                <button onClick={() => { localStorage.removeItem('VENTIO_LAST_TENANT'); localStorage.removeItem('VENTIO_API_URL'); window.location.reload(); }} style={{ marginLeft: 8, padding: '8px 12px', borderRadius: 6, background: '#6c757d', color: 'white', border: 'none' }}>Reiniciar pistas</button>
              </div>
              <p style={{ marginTop: 12, color: '#666' }}>Si el problema persiste, verifica tu conexión o utiliza el botón "Reiniciar pistas" para restaurar la configuración por defecto.</p>
            </div>
          ) : (
            <>
              {/* Tarjetas de resumen */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '14px',
                width: '100%',
                marginBottom: '18px'
              }}>
                <div style={{ ...cardStyle, position: 'relative' }}>
                  <div 
                    onClick={() => setShowSecretStats(!showSecretStats)}
                    style={{ position: 'absolute', top: 12, right: 12, cursor: 'pointer', color: '#94a3b8', fontSize: '14px' }}
                    title={showSecretStats ? "Ocultar" : "Mostrar"}
                  >
                    {showSecretStats ? <FaEyeSlash /> : <FaEye />}
                  </div>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>💰</div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Ventas Hoy</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {showSecretStats ? stats.todaySales : '****'}
                  </div>
                </div>
                <div style={{ ...cardStyle, position: 'relative' }}>
                  <div 
                    onClick={() => setShowSecretStats(!showSecretStats)}
                    style={{ position: 'absolute', top: 12, right: 12, cursor: 'pointer', color: '#94a3b8', fontSize: '14px' }}
                    title={showSecretStats ? "Ocultar" : "Mostrar"}
                  >
                    {showSecretStats ? <FaEyeSlash /> : <FaEye />}
                  </div>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>📊</div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Ingresos Hoy</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {showSecretStats ? `$${stats.todayRevenue.toFixed(2)}` : '****'}
                  </div>
                </div>
                <div style={cardStyle}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>📦</div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Total Productos</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.totalProducts}</div>
                </div>
                <div style={{ ...cardStyle, borderLeft: stats.lowStockCount > 0 ? '4px solid #ffc107' : 'none' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚠️</div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Stock Bajo</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: stats.lowStockCount > 0 ? '#ffc107' : '#333' }}>
                    {stats.lowStockCount}
                  </div>
                </div>
                <div style={cardStyle}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>🎯</div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Ticket Promedio</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>${stats.averageTicket.toFixed(2)}</div>
                </div>
                {/* Tarjeta 'Stock Crítico' eliminada a pedido */}
              </div>

              {/* Gráficos y análisis */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '16px',
                marginBottom: '18px'
              }}>
                <SalesChart />
                <TopProducts />
              </div>

              {/* Últimas ventas */}
              {recentSales.length > 0 ? (
                <>
                  {/* Desktop/Tableta: tabla clásica con scroll seguro */}
                  <div className="responsive-table hide-on-mobile" style={{
                    backgroundColor: 'white',
                    padding: '14px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    width: '100%',
                    marginBottom: '18px',
                    maxHeight: '40vh',
                    overflowY: 'auto'
                  }}>
                    <h3 style={{ marginBottom: '15px' }}>📋 Últimas 5 Ventas</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: '10px', textAlign: 'left' }}>Fecha</th>
                          <th style={{ padding: '10px', textAlign: 'left' }}>Items</th>
                          <th style={{ padding: '10px', textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentSales.map((sale, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ padding: '10px' }}>
                              {new Date(sale.createdAt || sale.date).toLocaleString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </td>
                            <td style={{ padding: '10px' }}>{sale.items.length} producto(s)</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                              ${Number(sale.total || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Móvil: tarjetas */}
                  <div className="show-on-mobile" style={{ display: 'grid', gap: '10px' }}>
                    <h3 style={{ margin: '6px 0' }}>📋 Últimas 5 Ventas</h3>
                    {recentSales.map((sale, idx) => (
                      <div key={idx} style={{ background: 'white', borderRadius: '10px', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', padding: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <div style={{ fontWeight: 600 }}>
                            {new Date(sale.createdAt || sale.date).toLocaleString('es-ES', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                          <div style={{ fontWeight: 700 }}>${Number(sale.total || 0).toFixed(2)}</div>
                        </div>
                        <div style={{ fontSize: 13, color: '#555' }}>{sale.items.length} producto(s)</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ background: 'white', padding: 14, borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.05)', marginBottom: 18 }}>
                  <h3 style={{ margin: 0 }}>📋 Últimas 5 Ventas</h3>
                  <p style={{ color: '#666', marginTop: 8 }}>No se encontraron ventas recientes.</p>
                </div>
              )}
              <PrintSettings open={showSettings} onClose={() => setShowSettings(false)} />
              {/* Help modal que se abre automáticamente para usuarios nuevos */}
              {typeof window !== 'undefined' && (
                <React.Suspense fallback={null}>
                  <HelpModal open={showHelp} onClose={() => {
                    const tenantId = tenant?._id || tenant?.id;
                    if (tenantId) try { localStorage.setItem(`help_shown_${tenantId}`, '1'); } catch (e) { }
                    setShowHelp(false);
                  }} />
                </React.Suspense>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// 🔹 Estilos de botones reutilizables
const buttonBase = {
  padding: '12px 24px',
  borderRadius: '8px',
  border: 'none',
  color: 'white',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 'bold',
  transition: 'all 0.2s ease',
};

// Compact buttons used on the left sidebar
const smallButton = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: 'none',
  color: 'white',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 700,
  textAlign: 'left',
  width: '100%',
  marginBottom: '8px'
};

const buttonGreen = {
  ...buttonBase,
  backgroundColor: '#28a745',
};

const buttonBlue = {
  ...buttonBase,
  backgroundColor: '#007bff',
};

const buttonCyan = {
  ...buttonBase,
  backgroundColor: '#17a2b8',
};

const buttonOrange = {
  ...buttonBase,
  backgroundColor: '#fd7e14',
};

const buttonTeal = {
  ...buttonBase,
  backgroundColor: '#20c997',
};

const buttonPurple = {
  ...buttonBase,
  backgroundColor: '#6f42c1',
};

const buttonRed = {
  ...buttonBase,
  backgroundColor: '#dc3545',
};

const cardStyle = {
  backgroundColor: 'white',
  padding: '20px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  textAlign: 'center',
};

export default Dashboard;
